/*
 * Personal Digital Spaces LLC ("COMPANY") CONFIDENTIAL
 * Unpublished Copyright (c) 2026 Personal Digital Spaces LLC, All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of COMPANY. The intellectual and technical concepts contained
 * herein are proprietary to COMPANY and may be covered by U.S. and Foreign Patents, patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from COMPANY.  Access to the source code contained herein is hereby forbidden to anyone except current COMPANY employees, managers, contractors or partners who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of  COMPANY.   ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import { useFetchItemsWithParamsV2 } from '@/hooks/fetch/useFetchItemsWithParamsV2'
import { getAIMetadataByLicensedAssetId } from '@/services/licensed-assets'
import { Box, Flex } from '@chakra-ui/react'
import React, { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import AIMetadataCard from './components/AIMetadataCard'
import ShareModal from './components/ShareModal'
import ResponsivePagination from 'react-responsive-pagination'
import { builtSuccessfullyString, getTitle } from './handlers'
import SelectAIMetadataByState from './components/SelectAIMetadataByState'
import CircleLoader from '@/components/loaders/CircleLoader'

const AIMetadata = ({ licensedAssetId, assetFileType }) => {
  const [urlSearchParams] = useSearchParams()
  const aiMetadataState = urlSearchParams.get('aiMetadataState') || builtSuccessfullyString
  const {
    itemsResponse: aiMetadata,
    loading,
    onChangePage,
    page,
    reloadItems,
    totalPages,
    setSearchParams
  } = useFetchItemsWithParamsV2({
    fetchService: getAIMetadataByLicensedAssetId,
    fetchParams: {
      licensedAssetId,
      aiMetadataState
    }
  })
  const [selectedAIMetadata, setSelectedAIMetadata] = useState(null)

  const handleChangeState = (state) => {
    setSearchParams((prevParams) => {
      const newParams = new URLSearchParams(prevParams)
      newParams.set('aiMetadataState', state)
      newParams.delete('page')

      return newParams
    })
  }
  const handlerSelectAIMetadata = (aiMetadataItem) => {
    setSelectedAIMetadata({ ...aiMetadataItem, fileType: assetFileType })
  }
  const handleCloseModal = () => {
    setSelectedAIMetadata(null)
  }

  if (loading) return <CircleLoader isCentered={true} />

  return (
    <Box flexGrow={1}>
      <Flex justifyContent="space-between" alignItems="center" mb="3">
        <h3 className="mb-3">
          {`${getTitle(aiMetadataState) || getTitle(builtSuccessfullyString)} (${aiMetadata.total})`}
        </h3>
        <SelectAIMetadataByState
          handleChangeState={handleChangeState}
          aiMetadataState={aiMetadataState}
        />
      </Flex>
      <Box
        display="flex"
        flexWrap="wrap"
        gap={3}
        mb="3"
      >
        { aiMetadata.data.map(aiMetadataItem => {
          return (
            <AIMetadataCard
              aiMetadataItem={aiMetadataItem}
              createdAt={aiMetadataItem.createdAt}
              embeddingModel={aiMetadataItem.embeddingModel}
              handleCloseModal={handleCloseModal}
              handlerSelectAIMetadata={handlerSelectAIMetadata}
              id={aiMetadataItem.id}
              key={aiMetadataItem.id}
              state={aiMetadataItem.state}
            />
          )
        })}
      </Box>
      { !!totalPages &&
        <ResponsivePagination
          current={+page}
          total={+totalPages}
          linkHref="omit"
          onPageChange={(page) => onChangePage(page)}
        />
      }
      { selectedAIMetadata &&
        <ShareModal
          reloadLicensedAsset={reloadItems}
          onCloseModal={handleCloseModal}
          aiMetadata={selectedAIMetadata}
        />
      }
    </Box>
  )
}

export default AIMetadata
